import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Color } from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import FontSize from "@tiptap/extension-font-size";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { toast } from "sonner";
import { format, subDays, parseISO, isWeekend, isBefore, startOfDay } from "date-fns";
import { CalendarDays, Save, Loader2, Lock, CheckCircle, Trash2, Plus, X, Bold, Palmtree, Stethoscope, PartyPopper, Calendar as CalendarIcon, Eye, Sparkles, Eraser, Type, AlignLeft, Hash, DollarSign, ToggleLeft, CircleDot, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

// Day type options
const DAY_TYPES = [
  { value: "", label: "Work Day", icon: CalendarDays, color: "text-blue-600" },
  { value: "sick_day", label: "Sick Day", icon: Stethoscope, color: "text-red-600" },
  { value: "holiday", label: "Holiday", icon: PartyPopper, color: "text-purple-600" },
  { value: "vacation", label: "Vacation", icon: Palmtree, color: "text-green-600" },
];

// Color palette for text
const COLORS = ["#000000", "#DC2626", "#EA580C", "#CA8A04", "#16A34A", "#0891B2", "#2563EB", "#7C3AED", "#C026D3"];

// Font sizes
const FONT_SIZES = [
  { value: "12px", label: "Small" },
  { value: "14px", label: "Normal" },
  { value: "18px", label: "Large" },
  { value: "24px", label: "X-Large" },
];

// Field type icons for visual display
const FIELD_TYPE_ICONS = {
  rich_text: FileText,
  text: Type,
  paragraph: AlignLeft,
  number: Hash,
  currency: DollarSign,
  true_false: ToggleLeft,
  yes_no: CircleDot,
};

// TipTap Editor Toolbar
function EditorToolbar({ editor, onClear }) {
  if (!editor) return null;
  const currentFontSize = editor.getAttributes("textStyle").fontSize || "14px";

  return (
    <div className="flex items-center gap-1 p-2 border-b border-slate-200 bg-slate-50 rounded-t-lg flex-wrap">
      <Button
        type="button"
        variant={editor.isActive("bold") ? "secondary" : "ghost"}
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className="h-8 w-8 p-0"
        title="Bold"
      >
        <Bold className="h-4 w-4" />
      </Button>

      <div className="h-6 w-px bg-slate-200 mx-1" />

      <Select
        value={currentFontSize}
        onValueChange={(size) => editor.chain().focus().setFontSize(size).run()}
      >
        <SelectTrigger className="h-8 w-24 text-xs">
          <SelectValue placeholder="Size" />
        </SelectTrigger>
        <SelectContent>
          {FONT_SIZES.map((size) => (
            <SelectItem key={size.value} value={size.value} className="text-xs">
              {size.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="h-6 w-px bg-slate-200 mx-1" />

      <div className="flex items-center gap-1">
        {COLORS.map((color) => (
          <button
            key={color}
            type="button"
            onClick={() => editor.chain().focus().setColor(color).run()}
            className={`w-5 h-5 rounded border-2 transition-all ${
              editor.isActive("textStyle", { color }) ? "border-slate-800 scale-110" : "border-transparent hover:border-slate-300"
            }`}
            style={{ backgroundColor: color }}
            title={color}
          />
        ))}
      </div>

      <div className="h-6 w-px bg-slate-200 mx-1" />

      {onClear && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            editor.commands.clearContent();
            onClear();
          }}
          className="h-8 px-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50"
          title="Clear text"
        >
          <Eraser className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

// Rich Text Editor Component
function RichTextEditor({ value, onChange, disabled, onClear }) {
  const editor = useEditor({
    extensions: [StarterKit, TextStyle, Color, FontSize],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "");
    }
  }, [value, editor]);

  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [disabled, editor]);

  return (
    <div className={`border rounded-lg overflow-hidden ${disabled ? "opacity-60 bg-slate-100" : "bg-white"}`}>
      {!disabled && <EditorToolbar editor={editor} onClear={onClear} />}
      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none p-3 min-h-[120px] focus:outline-none [&_.ProseMirror]:min-h-[100px] [&_.ProseMirror]:outline-none [&_.ProseMirror_p]:my-0 [&_.ProseMirror_p]:leading-relaxed"
      />
    </div>
  );
}

// File Attachments Component
function FileAttachments({ attachments, onAdd, onRemove, disabled }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.rtf', '.xls', '.xlsx', '.png', '.jpg', '.jpeg', '.gif', '.webp'];
  
  const isAllowedFile = (filename) => {
    const ext = filename.toLowerCase().substring(filename.lastIndexOf('.'));
    return allowedExtensions.includes(ext);
  };

  const isImageFile = (filename) => {
    const ext = filename.toLowerCase().substring(filename.lastIndexOf('.'));
    return ['.png', '.jpg', '.jpeg', '.gif', '.webp'].includes(ext);
  };

  const getFileIcon = (filename) => {
    const ext = filename.toLowerCase().substring(filename.lastIndexOf('.'));
    if (['.pdf'].includes(ext)) return '📄';
    if (['.doc', '.docx', '.rtf'].includes(ext)) return '📝';
    if (['.txt'].includes(ext)) return '📃';
    if (['.xls', '.xlsx'].includes(ext)) return '📊';
    return '📎';
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (!isAllowedFile(file.name)) { 
          toast.error(`${file.name} - File type not allowed`); 
          continue; 
        }
        if (file.size > 50 * 1024 * 1024) { toast.error(`${file.name} exceeds 50MB`); continue; }
        const formData = new FormData();
        formData.append("file", file);
        const response = await axios.post(`${API}/uploads`, formData, { headers: { "Content-Type": "multipart/form-data" } });
        onAdd({ id: response.data.id, filename: response.data.filename, original_name: response.data.original_name, url: response.data.url });
      }
    } catch { toast.error("Failed to upload file"); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const handleRemove = async (att) => {
    try { await axios.delete(`${API}/uploads/${att.id}`); onRemove(att.id); }
    catch { toast.error("Failed to delete file"); }
  };

  return (
    <div className="space-y-2 mt-2">
      {attachments.length > 0 && (
        <div className="space-y-2">
          {attachments.map((att) => (
            <div key={att.id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg border">
              {isImageFile(att.original_name) ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${att.url}`} alt={att.original_name} className="w-16 h-16 object-cover rounded" />
              ) : (
                <div className="w-16 h-16 flex items-center justify-center bg-slate-100 rounded text-2xl">
                  {getFileIcon(att.original_name)}
                </div>
              )}
              <p className="flex-1 text-sm font-medium text-slate-700 truncate">{att.original_name}</p>
              {!disabled && <Button variant="ghost" size="sm" onClick={() => handleRemove(att)} className="text-red-500 hover:text-red-700"><X className="h-4 w-4" /></Button>}
            </div>
          ))}
        </div>
      )}
      {!disabled && (
        <div>
          <input type="file" ref={fileInputRef} onChange={handleFileSelect} multiple accept=".pdf,.doc,.docx,.txt,.rtf,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp" className="hidden" />
          <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {uploading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Uploading...</> : <><Plus className="mr-2 h-4 w-4" />Attach Files</>}
          </Button>
          <span className="ml-2 text-xs text-slate-400">PDF, DOC, TXT, XLS, images (max 50MB)</span>
        </div>
      )}
    </div>
  );
}

// Dynamic Field Renderer Component
function DynamicField({ field, value, onChange, attachments, onAddAttachment, onRemoveAttachment, disabled }) {
  const fieldType = field.field_type || "rich_text";
  const Icon = FIELD_TYPE_ICONS[fieldType] || FileText;

  // Render different input types based on field_type
  const renderInput = () => {
    switch (fieldType) {
      case "text":
        return (
          <Input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            placeholder={field.description || `Enter ${field.label}`}
            data-testid={`field-${field.id}`}
          />
        );

      case "paragraph":
        return (
          <Textarea
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            placeholder={field.description || `Enter ${field.label}`}
            rows={4}
            className="resize-none"
            data-testid={`field-${field.id}`}
          />
        );

      case "number":
        return (
          <Input
            type="number"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            placeholder="Enter a number"
            data-testid={`field-${field.id}`}
          />
        );

      case "currency":
        return (
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
            <Input
              type="number"
              step="0.01"
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              disabled={disabled}
              placeholder="0.00"
              className="pl-7"
              data-testid={`field-${field.id}`}
            />
          </div>
        );

      case "true_false":
        return (
          <div className="flex items-center gap-3 py-2">
            <Switch
              checked={value === true || value === "true"}
              onCheckedChange={(checked) => onChange(checked)}
              disabled={disabled}
              data-testid={`field-${field.id}`}
            />
            <span className="text-sm text-slate-600">
              {value === true || value === "true" ? "True" : "False"}
            </span>
          </div>
        );

      case "yes_no":
        return (
          <div className="flex gap-3">
            <label
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                value === "yes" ? "border-green-500 bg-green-50" : "border-slate-200 hover:border-slate-300"
              } ${disabled ? "opacity-50 pointer-events-none" : ""}`}
            >
              <input
                type="radio"
                name={`field-${field.id}`}
                value="yes"
                checked={value === "yes"}
                onChange={() => onChange("yes")}
                disabled={disabled}
                className="sr-only"
              />
              <CheckCircle className={`h-4 w-4 ${value === "yes" ? "text-green-600" : "text-slate-400"}`} />
              <span className={`text-sm font-medium ${value === "yes" ? "text-green-700" : "text-slate-600"}`}>Yes</span>
            </label>
            <label
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                value === "no" ? "border-red-500 bg-red-50" : "border-slate-200 hover:border-slate-300"
              } ${disabled ? "opacity-50 pointer-events-none" : ""}`}
            >
              <input
                type="radio"
                name={`field-${field.id}`}
                value="no"
                checked={value === "no"}
                onChange={() => onChange("no")}
                disabled={disabled}
                className="sr-only"
              />
              <X className={`h-4 w-4 ${value === "no" ? "text-red-600" : "text-slate-400"}`} />
              <span className={`text-sm font-medium ${value === "no" ? "text-red-700" : "text-slate-600"}`}>No</span>
            </label>
          </div>
        );

      case "rich_text":
      default:
        return (
          <>
            {/* Quick tags if available */}
            {field.quick_tags && field.quick_tags.length > 0 && !disabled && (
              <div className="flex flex-wrap gap-2 py-2">
                {field.quick_tags.map((tag) => (
                  <button
                    key={tag.name}
                    type="button"
                    onClick={() => {
                      const newLine = `<strong>${tag.name}</strong>: \n`;
                      const cleaned = value ? value.replace(/<p><\/p>/g, '').replace(/<p><br><\/p>/g, '').trim() : '';
                      onChange(cleaned ? newLine + cleaned : newLine);
                    }}
                    className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${tag.color}`}
                    data-testid={`tag-${tag.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    + {tag.name}
                  </button>
                ))}
              </div>
            )}
            <RichTextEditor
              value={value || ""}
              onChange={onChange}
              disabled={disabled}
              onClear={() => onChange("")}
            />
          </>
        );
    }
  };

  return (
    <div className="space-y-2" data-testid={`dynamic-field-${field.id}`}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-slate-400" />
        <Label className="text-sm font-medium">
          {field.label}
          {field.required ? (
            <span className="text-red-500 ml-1">*</span>
          ) : (
            <span className="text-xs text-slate-400 ml-1">(Optional)</span>
          )}
        </Label>
      </div>
      {field.description && <p className="text-xs text-slate-500">{field.description}</p>}
      
      {renderInput()}
      
      {/* Attachments for rich_text and paragraph */}
      {(fieldType === "rich_text" || fieldType === "paragraph") && field.has_attachments && (
        <FileAttachments
          attachments={attachments || []}
          onAdd={onAddAttachment}
          onRemove={onRemoveAttachment}
          disabled={disabled}
        />
      )}
    </div>
  );
}

export default function DailyLogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const dateParam = searchParams.get("date");

  // Form definition state
  const [formDefinition, setFormDefinition] = useState(null);
  const [formLoading, setFormLoading] = useState(true);

  // Dynamic form data state
  const [formData, setFormData] = useState({});
  const [attachmentsData, setAttachmentsData] = useState({});

  // Date and log state
  const [selectedDate, setSelectedDate] = useState("");
  const [customDate, setCustomDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [dayType, setDayType] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isEditable, setIsEditable] = useState(true);
  const [existingLog, setExistingLog] = useState(null);
  const [isViewingPastLog, setIsViewingPastLog] = useState(false);

  const getAvailableDates = () => {
    const today = new Date();
    const dates = [];
    for (let i = 0; i <= 2; i++) {
      const date = subDays(today, i);
      const dateStr = format(date, "yyyy-MM-dd");
      const isWeekendDay = isWeekend(date);
      let label = i === 0 ? `Today (${format(date, "MMM d")})` : i === 1 ? `Yesterday (${format(date, "MMM d")})` : format(date, "MMM d");
      if (isWeekendDay) label += " - Optional";
      dates.push({ value: dateStr, label, isWeekend: isWeekendDay });
    }
    dates.push({ value: "custom", label: "Select other date...", isWeekend: false });
    return dates;
  };

  const availableDates = getAvailableDates();

  // Initialize date selection
  useEffect(() => {
    if (dateParam) {
      const presetDate = availableDates.find(d => d.value === dateParam);
      if (presetDate) {
        setSelectedDate(dateParam);
      } else {
        setSelectedDate("custom");
        setCustomDate(parseISO(dateParam));
      }
    } else {
      setSelectedDate(availableDates[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch user's assigned form
  useEffect(() => {
    const fetchForm = async () => {
      try {
        setFormLoading(true);
        const response = await axios.get(`${API}/auth/me`);
        const userId = response.data.id;
        const formResponse = await axios.get(`${API}/users/${userId}/form`);
        setFormDefinition(formResponse.data);
      } catch (error) {
        console.error("Failed to fetch form:", error);
        toast.error("Failed to load form definition");
      } finally {
        setFormLoading(false);
      }
    };
    fetchForm();
  }, []);

  const getActiveDate = useCallback(() => {
    if (selectedDate === "custom" && customDate) {
      return format(customDate, "yyyy-MM-dd");
    }
    return selectedDate !== "custom" ? selectedDate : null;
  }, [selectedDate, customDate]);

  const activeDate = getActiveDate();
  const selectedDateObj = activeDate ? parseISO(activeDate) : new Date();
  const isWeekendDay = isWeekend(selectedDateObj);
  const isNonWorkDay = dayType !== "";

  const checkIfPastLog = (date) => {
    const today = startOfDay(new Date());
    const logDate = startOfDay(parseISO(date));
    const twoDaysAgo = startOfDay(subDays(today, 2));
    return isBefore(logDate, twoDaysAgo);
  };

  // Fetch existing log for selected date
  useEffect(() => {
    if (activeDate && formDefinition) {
      const isPast = checkIfPastLog(activeDate);
      setIsViewingPastLog(isPast);
      fetchLog(activeDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDate, formDefinition]);

  const fetchLog = async (date) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/logs/my/${date}`);
      if (response.data) {
        // Map existing log data to form fields
        const logData = response.data;
        const newFormData = {};
        const newAttachmentsData = {};
        
        // Map known fields from old format
        if (formDefinition?.fields) {
          formDefinition.fields.forEach(field => {
            // Try to get value from log using field.id as key
            if (logData[field.id] !== undefined) {
              newFormData[field.id] = logData[field.id];
            }
            // Also check legacy field names
            if (logData[`${field.id}_attachments`]) {
              newAttachmentsData[field.id] = logData[`${field.id}_attachments`];
            }
          });
        }
        
        // Handle legacy format fields
        if (logData.tasks_today !== undefined) newFormData.tasks_today = logData.tasks_today;
        if (logData.challenges !== undefined) newFormData.challenges = logData.challenges;
        if (logData.plan_tomorrow !== undefined) newFormData.plan_tomorrow = logData.plan_tomorrow;
        if (logData.ai_tools_used !== undefined) newFormData.ai_tools_used = logData.ai_tools_used;
        
        if (logData.tasks_today_attachments) newAttachmentsData.tasks_today = logData.tasks_today_attachments;
        if (logData.challenges_attachments) newAttachmentsData.challenges = logData.challenges_attachments;
        if (logData.plan_tomorrow_attachments) newAttachmentsData.plan_tomorrow = logData.plan_tomorrow_attachments;
        if (logData.ai_tools_attachments) newAttachmentsData.ai_tools_used = logData.ai_tools_attachments;

        setFormData(newFormData);
        setAttachmentsData(newAttachmentsData);
        setDayType(logData.day_type || "");
        
        if (checkIfPastLog(date)) {
          setIsEditable(false);
        } else {
          setIsEditable(logData.is_editable);
        }
        setExistingLog(logData);
      } else {
        resetForm();
        setIsEditable(true);
      }
    } catch (error) {
      if (error.response?.status === 401) { window.location.href = "/login"; return; }
      resetForm();
      setIsEditable(true);
    } finally { setLoading(false); }
  };

  const resetForm = () => {
    setFormData({});
    setAttachmentsData({});
    setDayType("");
    setIsEditable(true);
    setExistingLog(null);
  };

  const handleDateChange = (date) => {
    if (date === "custom") {
      setSelectedDate("custom");
      setCalendarOpen(true);
    } else {
      setSelectedDate(date);
      setCustomDate(null);
      setSearchParams({ date });
    }
  };

  const handleCustomDateSelect = (date) => {
    if (date) {
      setCustomDate(date);
      setCalendarOpen(false);
      const dateStr = format(date, "yyyy-MM-dd");
      setSearchParams({ date: dateStr });
    }
  };

  const stripHtml = (html) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || "";
  };

  const handleSave = async () => {
    if (!activeDate) {
      toast.error("Please select a date");
      return;
    }

    // Validate required fields if work day
    if (!isNonWorkDay && formDefinition?.fields) {
      for (const field of formDefinition.fields) {
        if (field.required) {
          const value = formData[field.id];
          const isEmpty = !value || (typeof value === 'string' && !stripHtml(value).trim());
          if (isEmpty) {
            toast.error(`Please fill in '${field.label}'`);
            return;
          }
        }
      }
    }

    setSaving(true);
    try {
      // Build payload with dynamic fields
      const payload = {
        log_date: activeDate,
        day_type: dayType,
        form_id: formDefinition?.id,
      };

      // Add field values and attachments
      if (!isNonWorkDay && formDefinition?.fields) {
        formDefinition.fields.forEach(field => {
          payload[field.id] = formData[field.id] || "";
          if (field.has_attachments && attachmentsData[field.id]) {
            payload[`${field.id}_attachments`] = attachmentsData[field.id].map(a => a.id);
          }
        });
      }

      // Ensure legacy fields are included for backwards compatibility
      if (!payload.tasks_today && formData.tasks_today) payload.tasks_today = isNonWorkDay ? "" : formData.tasks_today;
      if (!payload.challenges && formData.challenges) payload.challenges = isNonWorkDay ? "" : formData.challenges;
      if (!payload.plan_tomorrow && formData.plan_tomorrow) payload.plan_tomorrow = isNonWorkDay ? "" : formData.plan_tomorrow;
      if (!payload.ai_tools_used && formData.ai_tools_used) payload.ai_tools_used = isNonWorkDay ? "" : formData.ai_tools_used;
      
      if (!payload.tasks_today_attachments && attachmentsData.tasks_today) {
        payload.tasks_today_attachments = isNonWorkDay ? [] : attachmentsData.tasks_today.map(a => a.id);
      }
      if (!payload.challenges_attachments && attachmentsData.challenges) {
        payload.challenges_attachments = isNonWorkDay ? [] : attachmentsData.challenges.map(a => a.id);
      }
      if (!payload.plan_tomorrow_attachments && attachmentsData.plan_tomorrow) {
        payload.plan_tomorrow_attachments = isNonWorkDay ? [] : attachmentsData.plan_tomorrow.map(a => a.id);
      }
      if (!payload.ai_tools_attachments && attachmentsData.ai_tools_used) {
        payload.ai_tools_attachments = isNonWorkDay ? [] : attachmentsData.ai_tools_used.map(a => a.id);
      }

      await axios.post(`${API}/logs/upsert`, payload);
      toast.success(existingLog ? "Log updated!" : "Log created!");
      setExistingLog({ ...existingLog, ...payload });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!activeDate) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/logs/my/${activeDate}`);
      toast.success("Log deleted!");
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  const getDateLabel = () => {
    if (!activeDate) return "Select a date";
    const selected = availableDates.find((d) => d.value === activeDate);
    if (selected) return selected.label;
    return format(parseISO(activeDate), "MMMM d, yyyy");
  };

  const getDateSelectorDisplay = () => {
    if (selectedDate === "custom" && customDate) {
      return format(customDate, "MMM d, yyyy");
    }
    const selected = availableDates.find((d) => d.value === selectedDate);
    return selected?.label || "Select date";
  };

  const selectedDayType = DAY_TYPES.find(d => d.value === dayType);

  const updateFieldValue = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  const addAttachment = (fieldId, attachment) => {
    setAttachmentsData(prev => ({
      ...prev,
      [fieldId]: [...(prev[fieldId] || []), attachment]
    }));
  };

  const removeAttachment = (fieldId, attachmentId) => {
    setAttachmentsData(prev => ({
      ...prev,
      [fieldId]: (prev[fieldId] || []).filter(a => a.id !== attachmentId)
    }));
  };

  // Loading state for form definition
  if (formLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-slate-500">Loading form...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {formDefinition?.name || "Daily Work Log"}
          </h1>
          <p className="text-slate-600 mt-1">{formDefinition?.description || "Record your tasks, challenges, and plans"}</p>
        </div>
        <div className="flex items-center gap-3">
          <CalendarDays className="h-5 w-5 text-slate-400" />
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <div>
                <Select value={selectedDate} onValueChange={handleDateChange}>
                  <SelectTrigger className="w-[220px]" data-testid="date-selector">
                    <SelectValue>{getDateSelectorDisplay()}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {availableDates.map((date) => (
                      <SelectItem key={date.value} value={date.value}>
                        {date.value === "custom" ? (
                          <span className="flex items-center gap-2">
                            <CalendarIcon className="h-4 w-4" />
                            {date.label}
                          </span>
                        ) : date.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={customDate}
                onSelect={handleCustomDateSelect}
                disabled={(date) => date > new Date()}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Viewing Past Log Notice */}
      {isViewingPastLog && existingLog && !isEditable && (
        <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <Eye className="h-5 w-5 text-blue-600" />
          <p className="text-blue-800 text-sm">
            <strong>Viewing past log:</strong> This log from {format(parseISO(activeDate), "MMMM d, yyyy")} is displayed in read-only mode.
          </p>
        </div>
      )}

      {/* Creating Past Log Notice */}
      {isViewingPastLog && !existingLog && !loading && isEditable && (
        <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
          <CalendarDays className="h-5 w-5 text-green-600" />
          <p className="text-green-800 text-sm">
            <strong>Creating past log:</strong> You can enter a log for {format(parseISO(activeDate), "MMMM d, yyyy")} since no log exists for this date.
          </p>
        </div>
      )}

      {/* Day Type Selector */}
      {isEditable && (
        <div className="p-4 bg-white border border-slate-200 rounded-lg shadow-sm">
          <Label className="text-sm font-medium mb-3 block">Day Type</Label>
          <div className="flex flex-wrap gap-3">
            {DAY_TYPES.map((type) => (
              <label
                key={type.value}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                  dayType === type.value
                    ? type.value === "" 
                      ? "border-blue-500 bg-blue-50" 
                      : type.value === "sick_day"
                      ? "border-red-500 bg-red-50"
                      : type.value === "holiday"
                      ? "border-purple-500 bg-purple-50"
                      : "border-green-500 bg-green-50"
                    : "border-slate-200 hover:border-slate-300 bg-white"
                }`}
                data-testid={`day-type-${type.value || 'workday'}`}
              >
                <input
                  type="radio"
                  name="dayType"
                  value={type.value}
                  checked={dayType === type.value}
                  onChange={(e) => setDayType(e.target.value)}
                  className="sr-only"
                />
                <type.icon className={`h-4 w-4 ${type.color}`} />
                <span className={`text-sm font-medium ${dayType === type.value ? type.color : "text-slate-700"}`}>
                  {type.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Non-work day banner */}
      {isNonWorkDay && selectedDayType && (
        <div className={`flex items-center gap-3 p-4 rounded-lg border ${
          dayType === "sick_day" ? "bg-red-50 border-red-200" :
          dayType === "holiday" ? "bg-purple-50 border-purple-200" :
          "bg-green-50 border-green-200"
        }`}>
          <selectedDayType.icon className={`h-5 w-5 ${selectedDayType.color}`} />
          <p className={`text-sm ${
            dayType === "sick_day" ? "text-red-800" :
            dayType === "holiday" ? "text-purple-800" :
            "text-green-800"
          }`}>
            <strong>{selectedDayType.label}:</strong> No work log required. Click &quot;Save Log&quot; to record this as a {selectedDayType.label.toLowerCase()}.
          </p>
        </div>
      )}

      {/* Weekend Notice */}
      {isWeekendDay && !isNonWorkDay && !isViewingPastLog && (
        <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <CalendarDays className="h-5 w-5 text-blue-600" />
          <p className="text-blue-800 text-sm"><strong>Weekend log:</strong> Optional. No reminders for weekends.</p>
        </div>
      )}

      {/* Read-only Notice */}
      {!isEditable && !isViewingPastLog && (
        <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <Lock className="h-5 w-5 text-amber-600" />
          <p className="text-amber-800 text-sm">Read-only. Logs older than 2 days cannot be edited.</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl">{getDateLabel()}</CardTitle>
                <CardDescription>
                  {existingLog ? (
                    <span className="flex items-center gap-1 text-green-600">
                      <CheckCircle className="h-4 w-4" />
                      {existingLog.day_type ? DAY_TYPES.find(d => d.value === existingLog.day_type)?.label || "Log" : "Log"} submitted
                    </span>
                  ) : "No log submitted yet"}
                </CardDescription>
              </div>
              {isEditable ? <span className="badge-editable">Editable</span> : <span className="badge-readonly">Read-only</span>}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {existingLog && isEditable && (
              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={resetForm}>
                  <Plus className="mr-2 h-4 w-4" />Clear & Start Fresh
                </Button>
              </div>
            )}

            {/* Dynamic Form Fields */}
            <div className={isNonWorkDay ? "opacity-50 pointer-events-none" : ""}>
              {formDefinition?.fields?.map((field, index) => (
                <div key={field.id} className={index > 0 ? "mt-6" : ""}>
                  <DynamicField
                    field={field}
                    value={formData[field.id]}
                    onChange={(value) => updateFieldValue(field.id, value)}
                    attachments={attachmentsData[field.id]}
                    onAddAttachment={(att) => addAttachment(field.id, att)}
                    onRemoveAttachment={(id) => removeAttachment(field.id, id)}
                    disabled={!isEditable || saving || isNonWorkDay}
                  />
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            {isEditable && (
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                <Button onClick={handleSave} disabled={saving} data-testid="save-log-button">
                  {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : <><Save className="mr-2 h-4 w-4" />Save Log</>}
                </Button>
                {existingLog && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50" disabled={deleting}>
                        {deleting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting...</> : <><Trash2 className="mr-2 h-4 w-4" />Delete Log</>}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this log?</AlertDialogTitle>
                        <AlertDialogDescription>This will permanently delete your log for {getDateLabel()}.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
